import SignaturePad from "@/components/SignaturePad";
import { ShieldCheck } from "lucide-react";
import { GUARDIAN_RELATIONSHIP_OPTIONS } from "@/lib/guardian";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/**
 * GuardianConsentSection - parent/guardian consent block for under-18 patients.
 * Shown identically on clinic devices and on public (emailed) form links.
 */
export default function GuardianConsentSection({ section, value, onChange, signatureRef, onSignatureChange }) {
  if (!section?.show) return null;

  const options = section.relationship_options?.length ? section.relationship_options : GUARDIAN_RELATIONSHIP_OPTIONS;
  const g = value || {};
  const set = (key, val) => onChange?.({ ...g, [key]: val });

  return (
    <div
      className="mt-8 rounded-2xl border border-[#205B5A]/25 bg-[#205B5A]/[0.04] p-4 sm:p-5"
      data-testid="guardian-consent-section"
    >
      <div className="flex items-start gap-2.5 mb-4">
        <ShieldCheck className="w-4 h-4 text-[#205B5A] flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="text-base text-[#1A1B1A]" style={H}>
            Parent or guardian consent
          </h2>
          <p className="text-sm text-[#4A4B4A] leading-relaxed mt-1" data-testid="guardian-consent-notice">
            {section.notice}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
            Parent or guardian full name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={g.name || ""}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Full name"
            data-testid="guardian-name-input"
            className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
            Relationship to patient <span className="text-red-400">*</span>
          </label>
          <select
            value={g.relationship || ""}
            onChange={(e) => set("relationship", e.target.value)}
            data-testid="guardian-relationship-select"
            className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
          >
            <option value="">Select relationship...</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        {g.relationship === "Other" && (
          <div>
            <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
              Please describe the relationship <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={g.relationship_other || ""}
              onChange={(e) => set("relationship_other", e.target.value)}
              placeholder="e.g. Aunt, Social worker"
              data-testid="guardian-relationship-other-input"
              className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
            />
          </div>
        )}

        <div data-testid="guardian-signature-wrapper">
          <SignaturePad
            ref={signatureRef}
            label="Parent or guardian signature"
            required
            onChange={onSignatureChange}
            initialMode="draw"
          />
        </div>

        <p className="text-xs text-[#4A4B4A] leading-relaxed">
          By signing above you confirm that you are the patient's parent or legal guardian and that you
          consent on their behalf.
        </p>
      </div>
    </div>
  );
}
